import SectionLabel from '../components/SectionLabel';
import TechBadge from '../components/TechBadge';
import CTAButton from '../components/CTAButton';
import useDocumentTitle from '../hooks/useDocumentTitle';

const homePoster = require('../Assert/1.jpg');

const tickerWords = [
  'CHAIN',
  'PYTHON',
  'PYTORCH',
  'TENSORFLOW',
  'REACT',
  'NODE.JS',
  'WEB3',
  'UNITY',
];

function Home() {
  useDocumentTitle('MMI — Agentic AI & Immersive Technology');

  return (
    <main className="home">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container hero-container">
          <div className="hero-grid" aria-label="MMI hero">
            <h1 className="hero-title">
              We Went Dark for <span className="hero-accent">10 Years.</span>
              <br />
              <span className="hero-subtitle">The Future Caught Up.</span>
            </h1>

            <div className="hero-cardWrap">
              <div className="hero-card" aria-hidden="true">
                <div className="hero-media">
                  <video
                    className="hero-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={homePoster}
                  >
                    <source src="/videos/home.mp4" type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>

            <p className="hero-description">
              MMI is back. After a decade of silent research, we emerge to redefine the
              intersection of artificial intelligence and human experience.
            </p>
          </div>
        </div>
      </section>

      {/* Arsenal Section */}
      <section className="arsenal-section">
        <div className="container">
          <div className="arsenal-header">
            <SectionLabel text="OUR ARSENAL" variant="arsenal" />
            <p className="arsenal-tagline">We don't chase tools. We pick what works.</p>
          </div>

          <div className="tech-ticker" aria-label="MMI tech ticker">
            <div className="tech-ticker__viewport">
              <div className="tech-ticker__track">
                {[...tickerWords, ...tickerWords].map((w, idx) => (
                  <TechBadge key={`${w}-${idx}`} techName={w} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-content">
            <SectionLabel text="OUR STORY" variant="story" />
            <h2 className="story-title">
              Born in Immersion. <span className="gradient-text">Reborn in Intelligence.</span>
            </h2>
            <p className="story-description">
              MMI returns with technologies that were once considered science fiction.
              Today, they are ready for global integration. We innovate without interference.
            </p>
          </div>
        </div>
      </section>

      {/* What We Build Section */}
      <section className="services-section">
        <div className="container">
          <div className="wwb-label-wrap">
            <div className="wwb-pill">WHAT WE BUILD</div>
          </div>

          <div className="wwb-cards" aria-label="What we build">
            <div className="wwb-card">
              <div className="wwb-card-icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 4c-2 0-3 1.5-3 3v1H5a2 2 0 0 0 0 4h1v1H5a2 2 0 0 0 0 4h1v1c0 1.5 1 3 3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M15 4c2 0 3 1.5 3 3v1h1a2 2 0 0 1 0 4h-1v1h1a2 2 0 0 1 0 4h-1v1c0 1.5-1 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M12 7v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M9 10h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M10 13h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="wwb-card-title">Agentic AI Systems</h3>
              <p className="wwb-card-body">Autonomous entities capable of complex reasoning and goal-setting.</p>
            </div>

            <div className="wwb-card">
              <div className="wwb-card-icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 7h4v4H7V7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M13 13h4v4h-4v-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M11 11l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M7 11v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="11" cy="11" r="2" stroke="currentColor" strokeWidth="1.6"/>
                  <circle cx="17" cy="17" r="2" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
              </div>
              <h3 className="wwb-card-title">AI Automation</h3>
              <p className="wwb-card-body">Streamlining enterprise operations with intelligent workflows.</p>
            </div>

            <div className="wwb-card">
              <div className="wwb-card-icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2 3.5 6.8V17.2L12 22l8.5-4.8V6.8L12 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M12 22V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M20.5 6.8 12 12 3.5 6.8" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="wwb-card-title">AR/VR Experiences</h3>
              <p className="wwb-card-body">Blurring the line between physical and digital spaces.</p>
            </div>

            <div className="wwb-card">
              <div className="wwb-card-icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M2 12h20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M12 3c2.5 4 2.5 14 0 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="wwb-card-title">Meta Applications</h3>
              <p className="wwb-card-body">High-fidelity spatial computing and obsidian-grade neural interfaces.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why MMI Section */}
      <section className="why-section">
        <div className="container">
          <SectionLabel text="Why MMI and Not Someone Else" />
          <div className="reasons-grid">
            <div className="reason-card">
              <div className="reason-number">01</div>
              <p className="reason-text">Founded in Immersive tech over a decade ago</p>
            </div>
            <div className="reason-card">
              <div className="reason-number">02</div>
              <p className="reason-text">Went deep, came back with real experience</p>
            </div>
            <div className="reason-card">
              <div className="reason-number">03</div>
              <p className="reason-text">We don't prototype and pitch — we build and ship</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <h2 className="cta-title">
              The Future Doesn't Wait.
              <br />
              <span className="gradient-text">Neither should you.</span>
            </h2>
            <p className="cta-description">Let's build something that matters</p>
            <CTAButton text="Book a Call" to="/contact" />
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
